// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BadgeModel } from "@insite/client-framework/Types/ApiModels";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Badge from "@insite/mobius/Badge";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip from "@pace/MobiusOverrides/Tooltip";
import React, { useState } from "react";
import { css } from "styled-components";

interface Props {
    badgeList: BadgeModel[];
    maxBadges: number;
    extendedStyles?: ProductImageBadgesStyles;
    numberOfColumns?: number;
}

export interface ProductImageBadgesStyles {
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    image?: LazyImageProps;
}

export const productImageBadgesStyles: ProductImageBadgesStyles = {
    imageContainer: {
        css: css`
            justify-content: left;
        `,
    },
    badgeImageProps: {
        style: {
            width: "100%",
            height: "auto",
        },
    },
    imageItem: {
        css: css`
            padding: 5px;
            justify-content: center;
        `,
    },
};

const ProductImageBadges = ({ badgeList, maxBadges, extendedStyles, numberOfColumns }: Props) => {
    const [styles] = useState(() => mergeToNew(productImageBadgesStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.container} data-test-selector="productImageBadges">
            <FlexWrapContainer {...styles.imageContainer}>
                {badgeList.slice(0, maxBadges).map(badge => (
                    <FlexItem key={badge.id} flexColumns={numberOfColumns ?? 4} {...styles.imageItem}>
                        {/* Xng-Changes: adding tooltip */}
                        <Tooltip
                            text={badge.imageAltText ? badge.imageAltText : badge.name}
                            triggerComponent={
                                <Badge
                                    type={badge.badgeType}
                                    value={badge.largeImageBadgePath}
                                    imageAltText={badge.imageAltText ? badge.imageAltText : badge.name}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            }
                        />
                    </FlexItem>
                ))}
            </FlexWrapContainer>
        </StyledWrapper>
    );
};

export default ProductImageBadges;
